import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { getUploadUrl } from '../utils/apiUrl';
import { getContrastStyles, getBestTextColor } from '../utils/colorContrast';
import NewStudentForm from './parent/NewStudentForm';
import EditStudentForm from './parent/EditStudentForm';
import ReturningStudentList from './parent/ReturningStudentList';
import ReturningStudentForm from './parent/ReturningStudentForm';
import ReturningStudentEnrollmentForm from './parent/ReturningStudentEnrollmentForm';
import MyStudentsList from './parent/MyStudentsList';
import StudentIntakeList from './parent/StudentIntakeList';
import StudentIntakeForm from './parent/StudentIntakeForm';
import EditStudent from './parent/EditStudent';
import ParentFormsDashboard from './parent/ParentFormsDashboard';
import HouseholdInformation from './parent/HouseholdInformation';
import RecentlyDeleted from './parent/RecentlyDeleted';
import GettingStarted from './parent/GettingStarted';
import SignatureField from './shared/SignatureField';
import './Dashboard.css';
import './SuperAdminDashboard.css';
import './ParentDashboard.css';

// Custom styles for the school lunch form
const schoolLunchStyles = `
  .school-lunch-form {
    max-width: 800px;
    margin: 0 auto;
  }

  .school-lunch-section {
    margin-bottom: 2rem;
    padding: 1.5rem;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
  }

  .school-lunch-title {
    margin-top: 0;
    margin-bottom: 0.5rem;
    color: #2c3e50;
    font-size: 1.3rem;
    font-weight: 600;
  }

  .school-lunch-field {
    display: flex;
    flex-direction: column;
    margin-bottom: 1rem;
  }

  .school-lunch-label {
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #333;
  }

  .school-lunch-input,
  .school-lunch-select,
  .school-lunch-textarea {
    padding: 0.75rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
    font-family: inherit;
  }

  .school-lunch-input:focus,
  .school-lunch-select:focus,
  .school-lunch-textarea:focus {
    outline: none;
    border-color: #3498db;
    box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
  }

  .school-lunch-checkbox-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    font-weight: normal;
  }

  .school-lunch-checkbox {
    margin: 0;
    cursor: pointer;
  }

  .school-lunch-child-section {
    margin-bottom: 15px;
    padding: 10px;
    background-color: #f9f9f9;
    border-radius: 3px;
  }

  .school-lunch-child-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
  }

  .school-lunch-child-title {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
  }

  .school-lunch-remove-btn {
    padding: 4px 8px;
    background-color: #dc3545;
    color: white;
    border: none;
    border-radius: 3px;
    cursor: pointer;
    font-size: 12px;
  }

  .school-lunch-add-btn {
    padding: 8px 16px;
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 3px;
    cursor: pointer;
    margin-top: 15px;
  }

  .school-lunch-submit-btn {
    padding: 12px 24px;
    background-color: #28a745;
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 16px;
    cursor: pointer;
    align-self: flex-start;
  }

  .school-lunch-submit-btn:hover {
    background-color: #218838;
  }

  /* Success message styles */
  .success-message {
    text-align: center;
    padding: 2rem;
    background-color: #f8fff8;
    border-radius: 8px;
    border: 1px solid #d4edda;
  }

  .success-icon {
    font-size: 4rem;
    margin-bottom: 1rem;
  }

  .success-title {
    font-size: 1.5rem;
    color: #155724;
    margin-bottom: 1rem;
  }

  .success-text {
    font-size: 1.1rem;
    margin-bottom: 2rem;
    color: #155724;
  }

  .success-subtitle {
    margin-bottom: 2rem;
    color: #666;
    font-size: 1rem;
  }

  /* Signature error styling */
  .signature-error {
    color: #e74c3c;
    font-size: 0.875rem;
    margin-top: 0.5rem;
    font-weight: 500;
  }

  /* Signature field styles */
  .signature-field {
    margin-bottom: 1.5rem;
  }

  .signature-canvas-container {
    position: relative;
    display: inline-block;
    border: 2px solid #ddd;
    border-radius: 4px;
    background: white;
    cursor: crosshair;
  }

  .signature-canvas {
    display: block;
    width: 100%;
    max-width: 600px;
    height: 200px;
    touch-action: none;
  }

  .signature-canvas-container:hover {
    border-color: #667eea;
  }

  .signature-clear-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    padding: 0.4rem 0.8rem;
    background: #dc3545;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.85rem;
    z-index: 10;
  }

  .signature-clear-btn:hover {
    background: #c82333;
  }

  .signature-hint {
    margin-top: 0.5rem;
    font-size: 0.85rem;
    color: #666;
    font-style: italic;
  }
`;

// Inject the styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = schoolLunchStyles;
  document.head.appendChild(styleSheet);
}

// Working school lunch form component (no external dependencies)
const SchoolLunchApplicationForm = () => {
  const navigate = useNavigate();
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [signatureError, setSignatureError] = useState('');
  const [caseNumberError, setCaseNumberError] = useState('');
  const [formData, setFormData] = useState({
    children: [{ first_name: '', middle_initial: '', last_name: '', grade: 'K' }],
    snap_tanf_fdpir: 'No',
    adult_members: [{
      name: '',
      earnings_amount: 0,
      earnings_frequency: '',
      public_assistance_amount: 0,
      public_assistance_frequency: '',
      other_income_amount: 0,
      other_income_frequency: ''
    }],
    total_household_members: 1,
    no_ssn: [],
    child_income_amount: 0,
    adult_signer_first_name: '',
    adult_signer_last_name: '',
    signature_date: '',
    signature: '',
    mailing_address_street: '',
    mailing_address_city: '',
    mailing_address_state: '',
    mailing_address_zip: '',
    phone: '',
    email: ''
  });

  const addChild = () => {
    setFormData({
      ...formData,
      children: [...formData.children, { first_name: '', middle_initial: '', last_name: '', grade: 'K' }]
    });
  };

  const removeChild = (index) => {
    if (formData.children.length > 1) {
      const newChildren = formData.children.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        children: newChildren
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate signature
    if (!formData.signature || !formData.signature.image) {
      setSignatureError('Please sign the application before submitting.');
      // Scroll to signature field
      document.getElementById('signature-section')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    // Clear any previous signature error
    setSignatureError('');

    // Validate case number when SNAP/TANF/FDPIR is Yes
    if (formData.snap_tanf_fdpir === 'Yes') {
      if (!formData.case_number || formData.case_number.trim() === '') {
        setCaseNumberError('Case Number is required when participating in SNAP, TANF, or FDPIR.');
        // Scroll to case number field
        document.getElementById('case-number-section')?.scrollIntoView({ behavior: 'smooth' });
        return;
      }
    }

    // Clear any previous case number error
    setCaseNumberError('');

    try {
      // Submit the form data to be stored for later PDF generation
      const submitResponse = await axios.post('/api/forms/school-lunch-application/submit', {
        formData: formData
      });

      if (submitResponse.data.success) {
        setSubmissionSuccess(true);
        setSignatureError(''); // Clear any signature error on success

        // Trigger dashboard refresh to show submitted status
        window.dispatchEvent(new Event('progressUpdated'));
      } else {
        alert('Error submitting application: ' + submitResponse.data.message);
      }
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleOkClick = () => {
    navigate('/parent/forms');
  };

  if (submissionSuccess) {
    return (
      <div className="school-lunch-form">
        <h2 className="success-title">Application Submitted Successfully!</h2>
        <div className="success-message">
          <div className="success-icon">✅</div>
          <p className="success-text">
            Your school lunch application has been submitted successfully!
          </p>
          <p className="success-subtitle">
            District administrators can now view your application and generate the PDF when needed.
          </p>
          <button
            onClick={handleOkClick}
            className="school-lunch-submit-btn"
          >
            OK
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="school-lunch-form">
      <h2>School Lunch Application</h2>
      <p>Complete this form to apply for free or reduced price school meals. Your application data will be submitted and stored for district administrators to review and generate PDFs. All fields are required unless marked optional.</p>

      <form onSubmit={handleSubmit} className="school-lunch-form">

        {/* Children Section */}
        <div className="school-lunch-section">
          <h3 className="school-lunch-title">Step 1: Children Information</h3>
          {formData.children.map((child, index) => (
            <div key={index} className="school-lunch-child-section">
              <div className="school-lunch-child-header">
                <h4 className="school-lunch-child-title">Child {index + 1}</h4>
                {formData.children.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeChild(index)}
                    className="school-lunch-remove-btn"
                  >
                    Remove
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  className="school-lunch-input"
                  placeholder="First Name"
                  value={child.first_name}
                  onChange={(e) => {
                    const newChildren = [...formData.children];
                    newChildren[index].first_name = e.target.value;
                    setFormData({...formData, children: newChildren});
                  }}
                  required
                />
                <input
                  type="text"
                  className="school-lunch-input"
                  placeholder="MI"
                  value={child.middle_initial}
                  maxLength="1"
                  onChange={(e) => {
                    const newChildren = [...formData.children];
                    newChildren[index].middle_initial = e.target.value;
                    setFormData({...formData, children: newChildren});
                  }}
                />
                <input
                  type="text"
                  className="school-lunch-input"
                  placeholder="Last Name"
                  value={child.last_name}
                  onChange={(e) => {
                    const newChildren = [...formData.children];
                    newChildren[index].last_name = e.target.value;
                    setFormData({...formData, children: newChildren});
                  }}
                  required
                />
                <select
                  className="school-lunch-select"
                  value={child.grade}
                  onChange={(e) => {
                    const newChildren = [...formData.children];
                    newChildren[index].grade = e.target.value;
                    setFormData({...formData, children: newChildren});
                  }}
                  required
                >
                  <option value="K">Kindergarten</option>
                  <option value="1">1st Grade</option>
                  <option value="2">2nd Grade</option>
                  <option value="3">3rd Grade</option>
                  <option value="4">4th Grade</option>
                  <option value="5">5th Grade</option>
                  <option value="6">6th Grade</option>
                  <option value="7">7th Grade</option>
                  <option value="8">8th Grade</option>
                  <option value="9">9th Grade</option>
                  <option value="10">10th Grade</option>
                  <option value="11">11th Grade</option>
                  <option value="12">12th Grade</option>
                </select>
              </div>
            </div>
          ))}

          <div style={{ marginTop: '15px' }}>
            <button
              type="button"
              onClick={addChild}
              className="school-lunch-add-btn"
            >
              + Add Another Child
            </button>
          </div>
        </div>

        {/* Household Income */}
        <div className="school-lunch-section">
          <h3 className="school-lunch-title">Step 2: Household Income</h3>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Do any household members participate in SNAP, TANF, or FDPIR?
            </label>
            <div>
              <label style={{ marginRight: '15px' }}>
                <input
                  type="radio"
                  name="snap_tanf_fdpir"
                  value="Yes"
                  checked={formData.snap_tanf_fdpir === 'Yes'}
                  onChange={(e) => {
                    setFormData({...formData, snap_tanf_fdpir: e.target.value});
                    if (caseNumberError) setCaseNumberError('');
                  }}
                /> Yes
              </label>
              <label>
                <input
                  type="radio"
                  name="snap_tanf_fdpir"
                  value="No"
                  checked={formData.snap_tanf_fdpir === 'No'}
                  onChange={(e) => {
                    setFormData({...formData, snap_tanf_fdpir: e.target.value});
                    if (caseNumberError) setCaseNumberError('');
                  }}
                /> No
              </label>
            </div>
          </div>

          {formData.snap_tanf_fdpir === 'Yes' && (
            <div id="case-number-section" style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Case Number
              </label>
              <input
                type="text"
                value={formData.case_number || ''}
                onChange={(e) => {
                  setFormData({...formData, case_number: e.target.value});
                  if (caseNumberError) setCaseNumberError('');
                }}
                style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '3px', width: '200px' }}
                required
              />
              {caseNumberError && (
                <div className="case-number-error" style={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                  {caseNumberError}
                </div>
              )}
            </div>
          )}

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Total Household Members (Children and Adults)
            </label>
            <input
              type="number"
              value={formData.total_household_members}
              onChange={(e) => setFormData({...formData, total_household_members: parseInt(e.target.value) || 1})}
              style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '3px', width: '300px' }}
              min="1"
              required
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Child Income (Total for ALL children)
            </label>
            <input
              type="number"
              value={formData.child_income_amount}
              onChange={(e) => setFormData({...formData, child_income_amount: parseFloat(e.target.value) || 0})}
              style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '3px', width: '250px' }}
              min="0"
              step="0.01"
            />
          </div>

          {formData.child_income_amount > 0 && (
            <div style={{ marginBottom: '15px' }}>
              <select
                value={formData.child_income_frequency || ''}
                onChange={(e) => setFormData({...formData, child_income_frequency: e.target.value})}
                style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '3px', width: '200px' }}
                required
              >
                <option value="">How often?</option>
                <option value="Weekly">Weekly</option>
                <option value="Every 2 Weeks">Every 2 Weeks</option>
                <option value="2x Month">2x Month</option>
                <option value="Monthly">Monthly</option>
                <option value="Annual">Annual</option>
              </select>
            </div>
          )}

          {/* Adult Household Members */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
              Adult Household Members (Income Information)
            </label>

            {formData.adult_members.map((adult, index) => (
              <div key={index} style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '3px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h4 style={{ margin: 0 }}>Adult {index + 1}</h4>
                  {formData.adult_members.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const newAdults = formData.adult_members.filter((_, i) => i !== index);
                        setFormData({...formData, adult_members: newAdults});
                      }}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>
                      Full Name
                    </label>
                    <input
                      type="text"
                      className="school-lunch-input"
                      value={adult.name}
                      onChange={(e) => {
                        const newAdults = [...formData.adult_members];
                        newAdults[index].name = e.target.value;
                        setFormData({...formData, adult_members: newAdults});
                      }}
                      placeholder="First and Last Name"
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>
                      Earnings from Work
                    </label>
                    <input
                      type="number"
                      className="school-lunch-input"
                      value={adult.earnings_amount || ''}
                      onChange={(e) => {
                        const newAdults = [...formData.adult_members];
                        newAdults[index].earnings_amount = parseFloat(e.target.value) || 0;
                        setFormData({...formData, adult_members: newAdults});
                      }}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>
                      How Often?
                    </label>
                    <select
                      className="school-lunch-select"
                      value={adult.earnings_frequency || ''}
                      onChange={(e) => {
                        const newAdults = [...formData.adult_members];
                        newAdults[index].earnings_frequency = e.target.value;
                        setFormData({...formData, adult_members: newAdults});
                      }}
                    >
                      <option value="">Select frequency</option>
                      <option value="Weekly">Weekly</option>
                      <option value="Every 2 Weeks">Every 2 Weeks</option>
                      <option value="2x Month">2x Month</option>
                      <option value="Monthly">Monthly</option>
                      <option value="Annual">Annual</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>
                      Other Income (Pensions, SSI, etc.)
                    </label>
                    <input
                      type="number"
                      className="school-lunch-input"
                      value={adult.other_income_amount || ''}
                      onChange={(e) => {
                        const newAdults = [...formData.adult_members];
                        newAdults[index].other_income_amount = parseFloat(e.target.value) || 0;
                        setFormData({...formData, adult_members: newAdults});
                      }}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>
                      How Often?
                    </label>
                    <select
                      className="school-lunch-select"
                      value={adult.other_income_frequency || ''}
                      onChange={(e) => {
                        const newAdults = [...formData.adult_members];
                        newAdults[index].other_income_frequency = e.target.value;
                        setFormData({...formData, adult_members: newAdults});
                      }}
                    >
                      <option value="">Select frequency</option>
                      <option value="Weekly">Weekly</option>
                      <option value="Every 2 Weeks">Every 2 Weeks</option>
                      <option value="2x Month">2x Month</option>
                      <option value="Monthly">Monthly</option>
                      <option value="Annual">Annual</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={() => {
                if (formData.adult_members.length < 5) {
                  setFormData({
                    ...formData,
                    adult_members: [...formData.adult_members, {
                      name: '',
                      earnings_amount: 0,
                      earnings_frequency: '',
                      public_assistance_amount: 0,
                      public_assistance_frequency: '',
                      other_income_amount: 0,
                      other_income_frequency: ''
                    }]
                  });
                }
              }}
              className="school-lunch-add-btn"
              disabled={formData.adult_members.length >= 5}
            >
              + Add Another Adult Member
            </button>
          </div>
        </div>

        {/* Contact Information */}
        <div className="school-lunch-section">
          <h3 className="school-lunch-title">Step 3: Contact Information</h3>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Adult Signer Name
            </label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="text"
                placeholder="First Name"
                value={formData.adult_signer_first_name}
                onChange={(e) => setFormData({...formData, adult_signer_first_name: e.target.value})}
                style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '3px', flex: 1 }}
                required
              />
              <input
                type="text"
                placeholder="Last Name"
                value={formData.adult_signer_last_name}
                onChange={(e) => setFormData({...formData, adult_signer_last_name: e.target.value})}
                style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '3px', flex: 1 }}
                required
              />
            </div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Signature Date
            </label>
            <input
              type="date"
              value={formData.signature_date}
              onChange={(e) => setFormData({...formData, signature_date: e.target.value})}
              style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '3px', width: '200px' }}
              required
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Mailing Address
            </label>
            <input
              type="text"
              placeholder="Street Address"
              value={formData.mailing_address_street}
              onChange={(e) => setFormData({...formData, mailing_address_street: e.target.value})}
              style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '3px', width: '100%' }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              City, State, ZIP
            </label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="text"
                placeholder="City"
                value={formData.mailing_address_city}
                onChange={(e) => setFormData({...formData, mailing_address_city: e.target.value})}
                style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '3px', flex: 2 }}
              />
              <input
                type="text"
                placeholder="State"
                value={formData.mailing_address_state}
                onChange={(e) => setFormData({...formData, mailing_address_state: e.target.value})}
                style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '3px', width: '80px' }}
              />
              <input
                type="text"
                placeholder="ZIP"
                value={formData.mailing_address_zip}
                onChange={(e) => setFormData({...formData, mailing_address_zip: e.target.value})}
                style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '3px', width: '100px' }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Contact Information
            </label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="text"
                placeholder="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '3px', flex: 1 }}
              />
              <input
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '3px', flex: 1 }}
              />
            </div>
          </div>

          {/* Signature Section */}
          <div id="signature-section" style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Parent/Guardian Signature *
            </label>
            <SignatureField
              value={formData.signature}
              onChange={(value) => {
                setFormData({...formData, signature: value});
                // Clear error when user starts signing
                if (signatureError) setSignatureError('');
              }}
              required={true}
              label="Parent/Guardian Signature"
              name="signature"
            />
            {signatureError && (
              <div className="signature-error">
                {signatureError}
              </div>
            )}
          </div>

          {/* SSN Section */}
          <div className="school-lunch-field">
            <label className="school-lunch-checkbox-label">
              <input
                type="checkbox"
                className="school-lunch-checkbox"
                checked={formData.no_ssn.includes('No Social Security Number')}
                onChange={(e) => setFormData({
                  ...formData,
                  no_ssn: e.target.checked ? ['No Social Security Number'] : []
                })}
              />
              Check if no Social Security Number
            </label>
          </div>

            {!formData.no_ssn.includes('No Social Security Number') && (
              <div className="school-lunch-field">
                <input
                  type="text"
                  className="school-lunch-input"
                  placeholder="Last 4 digits of SSN"
                  value={formData.ssn_last_four || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                    setFormData({...formData, ssn_last_four: value});
                  }}
                  maxLength="4"
                  pattern="[0-9]{4}"
                  required
                />
              </div>
            )}
        </div>

        <button
          type="submit"
          className="school-lunch-submit-btn"
        >
          Submit Application
        </button>
      </form>
    </div>
  );
};

const ParentDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [incompleteForms, setIncompleteForms] = useState([]);
  const [schoolLunchSubmission, setSchoolLunchSubmission] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Redirect to getting-started or student-intake based on dismissal preference
  useEffect(() => {
    if (location.pathname === '/parent' || location.pathname === '/parent/') {
      const gettingStartedDismissed = localStorage.getItem('gettingStartedDismissed');
      if (gettingStartedDismissed === 'true') {
        navigate('/parent/student-intake', { replace: true });
      } else {
        navigate('/parent/getting-started', { replace: true });
      }
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    checkIncompleteForm();
    checkSchoolLunchSubmission();
  }, [user, location.pathname]); // Re-check when route changes

  // Re-check when navigating to dashboard home or when window gains focus
  useEffect(() => {
    const handleFocus = () => {
      checkIncompleteForm();
      checkSchoolLunchSubmission();
    };
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkIncompleteForm();
        checkSchoolLunchSubmission();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Listen for progress updates from forms
    const handleProgressUpdate = () => {
      console.log('Progress update event received, refreshing incomplete forms');
      checkIncompleteForm();
      checkSchoolLunchSubmission();
    };

    window.addEventListener('progressUpdated', handleProgressUpdate);

    // Also check periodically when component is mounted
    const interval = setInterval(() => {
      checkIncompleteForm();
      checkSchoolLunchSubmission();
    }, 10000); // Check every 10 seconds

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('progressUpdated', handleProgressUpdate);
      clearInterval(interval);
    };
  }, []);

  const checkIncompleteForm = async () => {
    try {
      const response = await axios.get('/api/form-submissions/progress');

      // Filter for forms with actual progress
      const formsWithProgress = response.data.filter(form => (
        (!form.is_complete && form.progress_percentage > 0) ||
        (form.submission_data && Object.keys(form.submission_data).length > 0 && !form.is_complete)
      ));

      setIncompleteForms(formsWithProgress);
    } catch (error) {
      console.error('Failed to check incomplete forms:', error);
      setIncompleteForms([]);
    }
  };

  const checkSchoolLunchSubmission = async () => {
    try {
      // Check if there's an existing school lunch submission for this user
      const response = await axios.get('/api/form-submissions/progress');
      const schoolLunchForm = response.data.find(form =>
        form.form_instance_id === null &&
        form.submission_data &&
        form.submission_data._form_type === 'school-lunch-application' &&
        form.is_complete
      );
      setSchoolLunchSubmission(schoolLunchForm || null);
    } catch (error) {
      console.error('Failed to check school lunch submission:', error);
      setSchoolLunchSubmission(null);
    }
  };

  const handleDeleteIncompleteForm = async (formId, formName) => {
    const formType = formName || 'this form';
    if (!window.confirm(`Are you sure you want to delete the incomplete ${formType}? All progress will be lost.`)) {
      return;
    }

    try {
      await axios.delete(`/api/form-submissions/progress/${formId}`);
      setIncompleteForms(prev => prev.filter(form => form.id !== formId));
    } catch (error) {
      console.error('Failed to delete incomplete form:', error);
      alert('Failed to delete form progress');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const primaryColor = user?.primary_color || '#2c3e50';
  const secondaryColor = user?.secondary_color || '#ff9800'; // Default to orange if no secondary color
  const logoUrl = user?.logo_url;
  const subdomain = user?.subdomain;

  // Log logo info for debugging (can be removed in production)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && logoUrl) {
      console.log('[Logo] Loaded:', {
        logoUrl,
        subdomain,
        fullUrl: getUploadUrl(logoUrl, subdomain)
      });
    }
  }, [logoUrl, subdomain]);

  // Get contrast-optimized styles for header and alert bar
  const headerStyle = getContrastStyles(primaryColor);
  const alertBarStyle = getContrastStyles(secondaryColor);
  
  // Get text colors for CSS variables (for use in styled components)
  const headerTextColor = getBestTextColor(primaryColor);
  const alertBarTextColor = getBestTextColor(secondaryColor);

  return (
    <div className="dashboard-container">
      <header 
        className="dashboard-header" 
        style={headerStyle}
      >
        <div className="dashboard-header-content">
          <button 
            className="mobile-menu-toggle"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          {logoUrl && (
            <img 
              src={getUploadUrl(logoUrl, subdomain)} 
              alt={user?.district_name || 'District Logo'} 
              className="dashboard-logo"
              onError={(e) => {
                console.error('[Logo] Failed to load:', {
                  logoUrl,
                  subdomain,
                  fullUrl: getUploadUrl(logoUrl, subdomain),
                  error: 'Image load failed'
                });
                // Hide broken image
                e.target.style.display = 'none';
              }}
            />
          )}
          <h1>{user?.district_name || 'District'} - Parent Portal</h1>
        </div>
        <button onClick={handleLogout} className="btn-logout">Logout</button>
      </header>
      {incompleteForms.length > 0 && (
        <div className="incomplete-forms-alert" style={alertBarStyle}>
          <div className="incomplete-forms-alert-header">
            <span className="incomplete-forms-alert-icon">⚠️</span>
            <span>
              <strong>Incomplete Forms:</strong> You have {incompleteForms.length} incomplete form{incompleteForms.length > 1 ? 's' : ''}.
            </span>
          </div>
          <div className="incomplete-forms-alert-items">
            {incompleteForms.map(form => (
              <div 
                key={form.id} 
                className="incomplete-forms-alert-item"
                style={{
                  background: alertBarTextColor === '#ffffff' 
                    ? 'rgba(255,255,255,0.15)' 
                    : 'rgba(0,0,0,0.1)',
                  color: alertBarTextColor
                }}
              >
                <div className="incomplete-forms-alert-item-title">
                  {form.student_first_name
                    ? `New Student - ${form.student_first_name}`
                    : (form.instance_name || form.master_form_name || 'New Student Form')
                  }
                </div>
                <div className="incomplete-forms-alert-item-meta">
                  {form.progress_percentage}% complete
                  {form.updated_at && (
                    <span>
                      {' '}• Last saved: {new Date(form.updated_at).toLocaleString()}
                    </span>
                  )}
                </div>
                <div className="incomplete-forms-alert-item-actions">
                  <Link
                    to={form.form_instance_id ? `/parent/school-lunch/${form.form_instance_id}` : (form.student_intake_id ? `/parent/new-student/${form.student_intake_id}` : "/parent/new-student")}
                    className="incomplete-forms-alert-item-button incomplete-forms-alert-item-button-continue"
                    style={{
                      background: alertBarTextColor === '#ffffff' ? 'white' : '#f5f5f5',
                      color: secondaryColor
                    }}
                  >
                    Continue →
                  </Link>
                  <button
                    onClick={() => handleDeleteIncompleteForm(form.id, form.instance_name || form.master_form_name)}
                    className="incomplete-forms-alert-item-button incomplete-forms-alert-item-button-delete"
                    data-text-color={alertBarTextColor === '#ffffff' ? 'light' : 'dark'}
                    style={{
                      color: alertBarTextColor,
                      borderColor: alertBarTextColor
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {mobileMenuOpen && (
        <div className="mobile-menu-backdrop" onClick={closeMobileMenu}></div>
      )}
      <div className="dashboard-content">
        <nav className={`dashboard-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="sidebar-header">
            <h2>Menu</h2>
            <button 
              className="mobile-menu-close"
              onClick={closeMobileMenu}
              aria-label="Close menu"
            >
              ×
            </button>
          </div>
          <ul>
            <li>
              <Link to="/parent/getting-started" onClick={closeMobileMenu}>Getting Started</Link>
            </li>
            <li>
              <Link to="/parent/forms" onClick={closeMobileMenu}>View Forms</Link>
            </li>
            <li>
              <Link to="/parent/household-information" onClick={closeMobileMenu}>Household Information</Link>
            </li>
            <li>
              <Link to="/parent/student-intake" onClick={closeMobileMenu}>My Students</Link>
              <ul className="submenu">
                <li>
                  <Link to="/parent/student-intake/new" onClick={closeMobileMenu}>Add a New Student</Link>
                </li>
                <li><Link to="/parent/student-intake" onClick={closeMobileMenu}>Update Students</Link></li>
              </ul>
            </li>
            <li>
              <Link to="/parent/recently-deleted" onClick={closeMobileMenu}>Recently Deleted</Link>
            </li>
            {incompleteForms.filter(f => f.form_instance_id).map(form => (
              <li key={form.id}>
                <Link
                  to={`/parent/school-lunch/${form.form_instance_id}`}
                  onClick={closeMobileMenu}
                  className="sidebar-incomplete-form-link"
                  style={{ color: secondaryColor }}
                >
                  <span>⚠️</span>
                  <span>Continue {form.instance_name || form.master_form_name} ({form.progress_percentage}%)</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <main className="dashboard-main">
          <Routes>
            <Route path="/getting-started" element={<GettingStarted />} />
            <Route path="/" element={
              <div>
                <h2>Welcome to {user?.district_name || 'District'} Parent Portal</h2>
                <p>Use the menu to add a new student or manage existing students.</p>
                
                {incompleteForms.length > 0 && (
                  <div className="welcome-incomplete-forms">
                    <h3 className="welcome-incomplete-forms-title">
                      Incomplete Forms Found
                    </h3>
                    <div className="welcome-incomplete-forms-grid">
                      {incompleteForms.map(form => (
                        <div key={form.id} className="welcome-incomplete-forms-card">
                          <h4 className="welcome-incomplete-forms-card-title">
                            {form.instance_name || form.master_form_name || 'New Student Form'}
                          </h4>
                          <p className="welcome-incomplete-forms-card-meta">
                            {form.progress_percentage}% complete
                            {form.updated_at && (
                              <span> • Last saved: {new Date(form.updated_at).toLocaleString()}</span>
                            )}
                          </p>
                          <div className="welcome-incomplete-forms-card-actions">
                            <Link
                              to={form.form_instance_id ? `/parent/school-lunch/${form.form_instance_id}` : (form.student_intake_id ? `/parent/new-student/${form.student_intake_id}` : "/parent/new-student")}
                              className="btn-primary"
                            >
                              Continue Form
                            </Link>
                            <button
                              onClick={() => handleDeleteIncompleteForm(form.id, form.instance_name || form.master_form_name)}
                              className="btn-danger"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* School Lunch Application Status */}
                <div style={{
                  marginTop: '2rem',
                  padding: '1.5rem',
                  background: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px'
                }}>
                  <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>
                    School Lunch Application
                  </h3>
                  {schoolLunchSubmission ? (
                    <div style={{
                      padding: '1rem',
                      background: '#d4edda',
                      border: '1px solid #c3e6cb',
                      borderRadius: '4px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <strong style={{ color: '#155724' }}>✓ Application Submitted</strong>
                        <p style={{ margin: '0.5rem 0 0 0', color: '#155724' }}>
                          Submitted on {new Date(schoolLunchSubmission.submitted_at).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            const response = await axios.post('/api/forms/school-lunch-application/generate-pdf',
                              { submissionId: schoolLunchSubmission.id },
                              { responseType: 'blob' }
                            );
                            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                            window.open(url, '_blank');
                          } catch (error) {
                            alert('Failed to generate PDF: ' + (error.response?.data?.error || error.message));
                          }
                        }}
                        style={{
                          background: '#28a745',
                          color: 'white',
                          border: 'none',
                          padding: '0.5rem 1rem',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        View PDF
                      </button>
                    </div>
                  ) : (
                    <div style={{
                      padding: '1rem',
                      background: '#fff3cd',
                      border: '1px solid #ffeaa7',
                      borderRadius: '4px'
                    }}>
                      <p style={{ margin: '0 0 1rem 0', color: '#856404' }}>
                        Apply for free or reduced price school meals for your family.
                      </p>
                      <Link
                        to="/parent/school-lunch/school-lunch-application"
                        style={{
                          background: '#ffc107',
                          color: '#212529',
                          padding: '0.5rem 1rem',
                          borderRadius: '4px',
                          textDecoration: 'none',
                          fontWeight: 'bold',
                          display: 'inline-block'
                        }}
                      >
                        Start Application
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            } />
            <Route path="/forms" element={<ParentFormsDashboard />} />
            <Route path="/household-information" element={<HouseholdInformation />} />
            <Route path="/recently-deleted" element={<RecentlyDeleted />} />
            <Route path="/school-lunch/:formInstanceId" element={
              <div style={{ padding: '20px' }}>
                <h1>School Lunch Application</h1>
                <p>Complete this form to apply for free or reduced price school meals. The form will generate a PDF that matches the official Missouri School Lunch Application format.</p>
                <SchoolLunchApplicationForm />
              </div>
            } />
            <Route path="/students" element={<MyStudentsList />} />
            <Route path="/student-intake" element={<StudentIntakeList />} />
            <Route path="/student-intake/new" element={<StudentIntakeForm />} />
            <Route path="/new-student/:studentIntakeId" element={<NewStudentForm />} />
            <Route path="/new-student" element={<NewStudentForm />} />
            <Route path="/edit-student/:studentIntakeId" element={<EditStudentForm />} />
            <Route path="/returning-student-enrollment/:studentIntakeId" element={<ReturningStudentEnrollmentForm />} />
            <Route path="/returning-student-enrollment" element={<ReturningStudentEnrollmentForm />} />
            <Route path="/returning-student" element={<ReturningStudentForm />} />
            <Route path="/students/edit/:id" element={<EditStudent />} />
            <Route path="/returning-student" element={<ReturningStudentList />} />
            <Route path="/returning-student/:studentId" element={<ReturningStudentForm />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default ParentDashboard;

